"use client";

import { c as _c } from "react/compiler-runtime";
import { useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, BarChart3, Building2, Cpu, Gauge, GitMerge, LayoutDashboard, LogOut, Mail, Menu, MessageCircle, MessageSquare, Percent, Server, Sparkles } from "lucide-react";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { LanguageSwitcher } from "@/components/language-switcher";
import { Button } from "@/components/ui/button";
import { Sidebar, SidebarContent, SidebarFooter, SidebarGroup, SidebarGroupLabel, SidebarHeader, SidebarInset, SidebarMenu, SidebarMenuButton, SidebarMenuItem, SidebarProvider, SidebarTrigger, useSidebar } from "@/components/ui/sidebar";
import { useAuth } from "@/lib/auth-client";
import { useI18n } from "@llmgateway/i18n/react";
import { Logo } from "./ui/logo";
import type { ReactNode } from "react";
interface AdminShellProps {
  children: ReactNode;
}
function MobileHeader() {
  const $ = _c(17);
  const {
    toggleSidebar
  } = useSidebar();
  const {
    t
  } = useI18n();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Menu className="h-5 w-5" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== t) {
    t1 = t("admin.nav.toggleMenu");
    $[1] = t;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== t1) {
    t2 = <span className="sr-only">{t1}</span>;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t2 || $[6] !== toggleSidebar) {
    t3 = <Button variant="ghost" size="icon" className="h-9 w-9" onClick={toggleSidebar}>{t0}{t2}</Button>;
    $[5] = t2;
    $[6] = toggleSidebar;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className="flex h-7 w-7 items-center justify-center rounded-md bg-primary/10 text-primary"><Logo className="h-4 w-4" /></div>;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <span className="text-sm font-semibold leading-tight">doteb</span>;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t) {
    t6 = t("navigation.admin");
    $[10] = t;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t6) {
    t7 = <div className="flex items-center gap-2">{t4}<div className="flex flex-col">{t5}<span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">{t6}</span></div></div>;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== t3 || $[15] !== t7) {
    t8 = <header className="sticky top-0 z-40 flex h-14 items-center gap-4 border-b border-border/60 bg-background px-4 md:hidden">{t3}{t7}</header>;
    $[14] = t3;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  return t8;
}
export function AdminShell(t0) {
  const $ = _c(173);
  const {
    children
  } = t0;
  const pathname = usePathname();
  const router = useRouter();
  const {
    signOut
  } = useAuth();
  const queryClient = useQueryClient();
  const {
    t
  } = useI18n();
  const isAuthPage = pathname === "/login";
  const isDashboard = pathname === "/" || pathname === "";
  let t1;
  if ($[0] !== pathname) {
    t1 = pathname.startsWith("/organizations");
    $[0] = pathname;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const isOrganizations = t1;
  let t2;
  if ($[2] !== pathname) {
    t2 = pathname.startsWith("/devpass");
    $[2] = pathname;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const isDevpass = t2;
  let t3;
  if ($[4] !== pathname) {
    t3 = pathname.startsWith("/chat-plans");
    $[4] = pathname;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const isChatPlans = t3;
  let t4;
  if ($[6] !== pathname) {
    t4 = pathname.startsWith("/global-stats");
    $[6] = pathname;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const isGlobalStats = t4;
  const isDiscounts = pathname === "/discounts";
  const isRateLimits = pathname === "/rate-limits";
  const isProviders = pathname === "/providers";
  const isModels = pathname === "/models";
  const isModelProviderMappings = pathname === "/model-provider-mappings";
  let t5;
  if ($[8] !== pathname) {
    t5 = pathname.startsWith("/contact-submissions");
    $[8] = pathname;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const isContactSubmissions = t5;
  let t6;
  if ($[10] !== pathname) {
    t6 = pathname.startsWith("/chat-support-logs");
    $[10] = pathname;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const isChatSupportLogs = t6;
  let t7;
  if ($[12] !== pathname) {
    t7 = pathname.startsWith("/payment-failures");
    $[12] = pathname;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const isPaymentFailures = t7;
  let t8;
  if ($[14] !== queryClient || $[15] !== router || $[16] !== signOut) {
    t8 = async () => {
      await signOut({
        fetchOptions: {
          onSuccess: () => {
            queryClient.clear();
            router.push("/login");
          }
        }
      });
    };
    $[14] = queryClient;
    $[15] = router;
    $[16] = signOut;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  const handleSignOut = t8;
  if (isAuthPage) {
    let t9;
    if ($[18] !== children) {
      t9 = <>{children}</>;
      $[18] = children;
      $[19] = t9;
    } else {
      t9 = $[19];
    }
    return t9;
  }
  let t9;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <div className="flex h-7 w-7 items-center justify-center rounded-md bg-primary/10 text-primary"><Logo className="h-4 w-4" /></div>;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <span className="text-sm font-semibold leading-tight">doteb</span>;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== t) {
    t11 = t("navigation.admin");
    $[22] = t;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== t11) {
    t12 = <div className="flex items-center gap-2 px-1">{t9}<div className="flex flex-col">{t10}<span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{t11}</span></div></div>;
    $[24] = t11;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <SidebarTrigger className="hidden md:flex" />;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== t12) {
    t14 = <SidebarHeader className="border-b border-sidebar-border/60"><div className="flex h-12 items-center justify-between px-2">{t12}{t13}</div></SidebarHeader>;
    $[27] = t12;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] !== t) {
    t15 = t("admin.nav.main");
    $[29] = t;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  let t16;
  if ($[31] !== t15) {
    t16 = <SidebarGroupLabel>{t15}</SidebarGroupLabel>;
    $[31] = t15;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  let t17;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <LayoutDashboard className="h-4 w-4" />;
    $[33] = t17;
  } else {
    t17 = $[33];
  }
  let t18;
  if ($[34] !== t) {
    t18 = t("admin.nav.dashboard");
    $[34] = t;
    $[35] = t18;
  } else {
    t18 = $[35];
  }
  let t19;
  if ($[36] !== t18) {
    t19 = <span>{t18}</span>;
    $[36] = t18;
    $[37] = t19;
  } else {
    t19 = $[37];
  }
  let t20;
  if ($[38] !== isDashboard || $[39] !== t19) {
    t20 = <SidebarMenuItem><Link href="/" className="block"><SidebarMenuButton isActive={isDashboard} size="lg">{t17}{t19}</SidebarMenuButton></Link></SidebarMenuItem>;
    $[38] = isDashboard;
    $[39] = t19;
    $[40] = t20;
  } else {
    t20 = $[40];
  }
  let t21;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <Building2 className="h-4 w-4" />;
    $[41] = t21;
  } else {
    t21 = $[41];
  }
  let t22;
  if ($[42] !== t) {
    t22 = t("admin.nav.organizations");
    $[42] = t;
    $[43] = t22;
  } else {
    t22 = $[43];
  }
  let t23;
  if ($[44] !== t22) {
    t23 = <span>{t22}</span>;
    $[44] = t22;
    $[45] = t23;
  } else {
    t23 = $[45];
  }
  let t24;
  if ($[46] !== isOrganizations || $[47] !== t23) {
    t24 = <SidebarMenuItem><Link href="/organizations" className="block"><SidebarMenuButton isActive={isOrganizations} size="lg">{t21}{t23}</SidebarMenuButton></Link></SidebarMenuItem>;
    $[46] = isOrganizations;
    $[47] = t23;
    $[48] = t24;
  } else {
    t24 = $[48];
  }
  let t25;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <Sparkles className="h-4 w-4" />;
    $[49] = t25;
  } else {
    t25 = $[49];
  }
  let t26;
  if ($[50] !== t) {
    t26 = t("admin.nav.devpass");
    $[50] = t;
    $[51] = t26;
  } else {
    t26 = $[51];
  }
  let t27;
  if ($[52] !== t26) {
    t27 = <span>{t26}</span>;
    $[52] = t26;
    $[53] = t27;
  } else {
    t27 = $[53];
  }
  let t28;
  if ($[54] !== isDevpass || $[55] !== t27) {
    t28 = <SidebarMenuItem><Link href="/devpass" className="block"><SidebarMenuButton isActive={isDevpass} size="lg">{t25}{t27}</SidebarMenuButton></Link></SidebarMenuItem>;
    $[54] = isDevpass;
    $[55] = t27;
    $[56] = t28;
  } else {
    t28 = $[56];
  }
  let t29;
  if ($[57] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = <MessageSquare className="h-4 w-4" />;
    $[57] = t29;
  } else {
    t29 = $[57];
  }
  let t30;
  if ($[58] !== t) {
    t30 = t("admin.nav.chatPlans");
    $[58] = t;
    $[59] = t30;
  } else {
    t30 = $[59];
  }
  let t31;
  if ($[60] !== t30) {
    t31 = <span>{t30}</span>;
    $[60] = t30;
    $[61] = t31;
  } else {
    t31 = $[61];
  }
  let t32;
  if ($[62] !== isChatPlans || $[63] !== t31) {
    t32 = <SidebarMenuItem><Link href="/chat-plans" className="block"><SidebarMenuButton isActive={isChatPlans} size="lg">{t29}{t31}</SidebarMenuButton></Link></SidebarMenuItem>;
    $[62] = isChatPlans;
    $[63] = t31;
    $[64] = t32;
  } else {
    t32 = $[64];
  }
  let t33;
  if ($[65] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = <BarChart3 className="h-4 w-4" />;
    $[65] = t33;
  } else {
    t33 = $[65];
  }
  let t34;
  if ($[66] !== t) {
    t34 = t("admin.nav.globalStats");
    $[66] = t;
    $[67] = t34;
  } else {
    t34 = $[67];
  }
  let t35;
  if ($[68] !== t34) {
    t35 = <span>{t34}</span>;
    $[68] = t34;
    $[69] = t35;
  } else {
    t35 = $[69];
  }
  let t36;
  if ($[70] !== isGlobalStats || $[71] !== t35) {
    t36 = <SidebarMenuItem><Link href="/global-stats" className="block"><SidebarMenuButton isActive={isGlobalStats} size="lg">{t33}{t35}</SidebarMenuButton></Link></SidebarMenuItem>;
    $[70] = isGlobalStats;
    $[71] = t35;
    $[72] = t36;
  } else {
    t36 = $[72];
  }
  let t37;
  if ($[73] === Symbol.for("react.memo_cache_sentinel")) {
    t37 = <Percent className="h-4 w-4" />;
    $[73] = t37;
  } else {
    t37 = $[73];
  }
  let t38;
  if ($[74] !== t) {
    t38 = t("admin.nav.globalDiscounts");
    $[74] = t;
    $[75] = t38;
  } else {
    t38 = $[75];
  }
  let t39;
  if ($[76] !== t38) {
    t39 = <span>{t38}</span>;
    $[76] = t38;
    $[77] = t39;
  } else {
    t39 = $[77];
  }
  let t40;
  if ($[78] !== isDiscounts || $[79] !== t39) {
    t40 = <SidebarMenuItem><Link href="/discounts" className="block"><SidebarMenuButton isActive={isDiscounts} size="lg">{t37}{t39}</SidebarMenuButton></Link></SidebarMenuItem>;
    $[78] = isDiscounts;
    $[79] = t39;
    $[80] = t40;
  } else {
    t40 = $[80];
  }
  let t41;
  if ($[81] === Symbol.for("react.memo_cache_sentinel")) {
    t41 = <Gauge className="h-4 w-4" />;
    $[81] = t41;
  } else {
    t41 = $[81];
  }
  let t42;
  if ($[82] !== t) {
    t42 = t("admin.nav.globalRateLimits");
    $[82] = t;
    $[83] = t42;
  } else {
    t42 = $[83];
  }
  let t43;
  if ($[84] !== t42) {
    t43 = <span>{t42}</span>;
    $[84] = t42;
    $[85] = t43;
  } else {
    t43 = $[85];
  }
  let t44;
  if ($[86] !== isRateLimits || $[87] !== t43) {
    t44 = <SidebarMenuItem><Link href="/rate-limits" className="block"><SidebarMenuButton isActive={isRateLimits} size="lg">{t41}{t43}</SidebarMenuButton></Link></SidebarMenuItem>;
    $[86] = isRateLimits;
    $[87] = t43;
    $[88] = t44;
  } else {
    t44 = $[88];
  }
  let t45;
  if ($[89] === Symbol.for("react.memo_cache_sentinel")) {
    t45 = <Server className="h-4 w-4" />;
    $[89] = t45;
  } else {
    t45 = $[89];
  }
  let t46;
  if ($[90] !== t) {
    t46 = t("admin.nav.providers");
    $[90] = t;
    $[91] = t46;
  } else {
    t46 = $[91];
  }
  let t47;
  if ($[92] !== t46) {
    t47 = <span>{t46}</span>;
    $[92] = t46;
    $[93] = t47;
  } else {
    t47 = $[93];
  }
  let t48;
  if ($[94] !== isProviders || $[95] !== t47) {
    t48 = <SidebarMenuItem><Link href="/providers" className="block"><SidebarMenuButton isActive={isProviders} size="lg">{t45}{t47}</SidebarMenuButton></Link></SidebarMenuItem>;
    $[94] = isProviders;
    $[95] = t47;
    $[96] = t48;
  } else {
    t48 = $[96];
  }
  let t49;
  if ($[97] === Symbol.for("react.memo_cache_sentinel")) {
    t49 = <Cpu className="h-4 w-4" />;
    $[97] = t49;
  } else {
    t49 = $[97];
  }
  let t50;
  if ($[98] !== t) {
    t50 = t("admin.nav.models");
    $[98] = t;
    $[99] = t50;
  } else {
    t50 = $[99];
  }
  let t51;
  if ($[100] !== t50) {
    t51 = <span>{t50}</span>;
    $[100] = t50;
    $[101] = t51;
  } else {
    t51 = $[101];
  }
  let t52;
  if ($[102] !== isModels || $[103] !== t51) {
    t52 = <SidebarMenuItem><Link href="/models" className="block"><SidebarMenuButton isActive={isModels} size="lg">{t49}{t51}</SidebarMenuButton></Link></SidebarMenuItem>;
    $[102] = isModels;
    $[103] = t51;
    $[104] = t52;
  } else {
    t52 = $[104];
  }
  let t53;
  if ($[105] === Symbol.for("react.memo_cache_sentinel")) {
    t53 = <GitMerge className="h-4 w-4" />;
    $[105] = t53;
  } else {
    t53 = $[105];
  }
  let t54;
  if ($[106] !== t) {
    t54 = t("admin.nav.modelMappings");
    $[106] = t;
    $[107] = t54;
  } else {
    t54 = $[107];
  }
  let t55;
  if ($[108] !== t54) {
    t55 = <span>{t54}</span>;
    $[108] = t54;
    $[109] = t55;
  } else {
    t55 = $[109];
  }
  let t56;
  if ($[110] !== isModelProviderMappings || $[111] !== t55) {
    t56 = <SidebarMenuItem><Link href="/model-provider-mappings" className="block"><SidebarMenuButton isActive={isModelProviderMappings} size="lg">{t53}{t55}</SidebarMenuButton></Link></SidebarMenuItem>;
    $[110] = isModelProviderMappings;
    $[111] = t55;
    $[112] = t56;
  } else {
    t56 = $[112];
  }
  let t57;
  if ($[113] === Symbol.for("react.memo_cache_sentinel")) {
    t57 = <Mail className="h-4 w-4" />;
    $[113] = t57;
  } else {
    t57 = $[113];
  }
  let t58;
  if ($[114] !== t) {
    t58 = t("admin.nav.contactSubmissions");
    $[114] = t;
    $[115] = t58;
  } else {
    t58 = $[115];
  }
  let t59;
  if ($[116] !== t58) {
    t59 = <span>{t58}</span>;
    $[116] = t58;
    $[117] = t59;
  } else {
    t59 = $[117];
  }
  let t60;
  if ($[118] !== isContactSubmissions || $[119] !== t59) {
    t60 = <SidebarMenuItem><Link href="/contact-submissions" className="block"><SidebarMenuButton isActive={isContactSubmissions} size="lg">{t57}{t59}</SidebarMenuButton></Link></SidebarMenuItem>;
    $[118] = isContactSubmissions;
    $[119] = t59;
    $[120] = t60;
  } else {
    t60 = $[120];
  }
  let t61;
  if ($[121] === Symbol.for("react.memo_cache_sentinel")) {
    t61 = <MessageCircle className="h-4 w-4" />;
    $[121] = t61;
  } else {
    t61 = $[121];
  }
  let t62;
  if ($[122] !== t) {
    t62 = t("admin.nav.chatSupportLogs");
    $[122] = t;
    $[123] = t62;
  } else {
    t62 = $[123];
  }
  let t63;
  if ($[124] !== t62) {
    t63 = <span>{t62}</span>;
    $[124] = t62;
    $[125] = t63;
  } else {
    t63 = $[125];
  }
  let t64;
  if ($[126] !== isChatSupportLogs || $[127] !== t63) {
    t64 = <SidebarMenuItem><Link href="/chat-support-logs" className="block"><SidebarMenuButton isActive={isChatSupportLogs} size="lg">{t61}{t63}</SidebarMenuButton></Link></SidebarMenuItem>;
    $[126] = isChatSupportLogs;
    $[127] = t63;
    $[128] = t64;
  } else {
    t64 = $[128];
  }
  let t65;
  if ($[129] === Symbol.for("react.memo_cache_sentinel")) {
    t65 = <AlertTriangle className="h-4 w-4" />;
    $[129] = t65;
  } else {
    t65 = $[129];
  }
  let t66;
  if ($[130] !== t) {
    t66 = t("admin.nav.paymentFailures");
    $[130] = t;
    $[131] = t66;
  } else {
    t66 = $[131];
  }
  let t67;
  if ($[132] !== t66) {
    t67 = <span>{t66}</span>;
    $[132] = t66;
    $[133] = t67;
  } else {
    t67 = $[133];
  }
  let t68;
  if ($[134] !== isPaymentFailures || $[135] !== t67) {
    t68 = <SidebarMenuItem><Link href="/payment-failures" className="block"><SidebarMenuButton isActive={isPaymentFailures} size="lg">{t65}{t67}</SidebarMenuButton></Link></SidebarMenuItem>;
    $[134] = isPaymentFailures;
    $[135] = t67;
    $[136] = t68;
  } else {
    t68 = $[136];
  }
  let t69;
  if ($[137] !== t20 || $[138] !== t24 || $[139] !== t28 || $[140] !== t32 || $[141] !== t36 || $[142] !== t40 || $[143] !== t44 || $[144] !== t48 || $[145] !== t52 || $[146] !== t56 || $[147] !== t60 || $[148] !== t64 || $[149] !== t68) {
    t69 = <SidebarMenu>{t20}{t24}{t28}{t32}{t36}{t40}{t44}{t48}{t52}{t56}{t60}{t64}{t68}</SidebarMenu>;
    $[137] = t20;
    $[138] = t24;
    $[139] = t28;
    $[140] = t32;
    $[141] = t36;
    $[142] = t40;
    $[143] = t44;
    $[144] = t48;
    $[145] = t52;
    $[146] = t56;
    $[147] = t60;
    $[148] = t64;
    $[149] = t68;
    $[150] = t69;
  } else {
    t69 = $[150];
  }
  let t70;
  if ($[151] !== t16 || $[152] !== t69) {
    t70 = <SidebarContent><SidebarGroup>{t16}{t69}</SidebarGroup></SidebarContent>;
    $[151] = t16;
    $[152] = t69;
    $[153] = t70;
  } else {
    t70 = $[153];
  }
  let t71;
  if ($[154] === Symbol.for("react.memo_cache_sentinel")) {
    t71 = <LanguageSwitcher className="w-full" />;
    $[154] = t71;
  } else {
    t71 = $[154];
  }
  let t72;
  if ($[155] === Symbol.for("react.memo_cache_sentinel")) {
    t72 = <LogOut className="h-3.5 w-3.5" />;
    $[155] = t72;
  } else {
    t72 = $[155];
  }
  let t73;
  if ($[156] !== t) {
    t73 = t("auth.signOut");
    $[156] = t;
    $[157] = t73;
  } else {
    t73 = $[157];
  }
  let t74;
  if ($[158] !== t73) {
    t74 = <span>{t73}</span>;
    $[158] = t73;
    $[159] = t74;
  } else {
    t74 = $[159];
  }
  let t75;
  if ($[160] !== handleSignOut || $[161] !== t74) {
    t75 = <SidebarFooter className="border-t border-sidebar-border/60">{t71}<Button variant="ghost" size="sm" className="w-full justify-start gap-2 text-xs text-muted-foreground" onClick={handleSignOut}>{t72}{t74}</Button></SidebarFooter>;
    $[160] = handleSignOut;
    $[161] = t74;
    $[162] = t75;
  } else {
    t75 = $[162];
  }
  let t76;
  if ($[163] !== t14 || $[164] !== t70 || $[165] !== t75) {
    t76 = <Sidebar variant="inset">{t14}{t70}{t75}</Sidebar>;
    $[163] = t14;
    $[164] = t70;
    $[165] = t75;
    $[166] = t76;
  } else {
    t76 = $[166];
  }
  let t77;
  if ($[167] === Symbol.for("react.memo_cache_sentinel")) {
    t77 = <MobileHeader />;
    $[167] = t77;
  } else {
    t77 = $[167];
  }
  let t78;
  if ($[168] !== children) {
    t78 = <SidebarInset>{t77}{children}</SidebarInset>;
    $[168] = children;
    $[169] = t78;
  } else {
    t78 = $[169];
  }
  let t79;
  if ($[170] !== t76 || $[171] !== t78) {
    t79 = <SidebarProvider>{t76}{t78}</SidebarProvider>;
    $[170] = t76;
    $[171] = t78;
    $[172] = t79;
  } else {
    t79 = $[172];
  }
  return t79;
}